import { Checkbox, FormControlLabel, Tab, Tabs } from '@mui/material';
import { Tooltip } from '@trucktrax/trucktrax-common';
import { DriverDto, PermissionAccess, PlantDto, ProductLine } from '@trucktrax/trucktrax-ts-common';
import cx from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { DATASOURCE, TRUCK_PROJECTIONS } from '../../../constants/apiConstants';
import { ADMIN_KEYS, ADMIN_LABELS, NOT_AVAILABLE } from '../../../constants/appConstants';
import { TRUCKS_TEXT, VEHICLES_TEXT } from '../../../constants/navConstants';
import { setReduxDriverList } from '../../../services/driversService';
import { ConnectedDispatchFunction } from '../../../types';
import { accessValue, addDataTest, addHeaderDataTest } from '../../../util/adminUtil';
import { getGeotraxBaseUrl } from '../../../util/apiUtil';
import { setDataTableQueryParams } from '../../../store/actions/dataTableActions';
import DataTableContent from '../../shared/DataTableContent';
import TrucksAddModal from './TrucksAddModal';
import styles from './TrucksView.module.css';

export interface TrucksViewState {
  isOpen: boolean;
  isCloseRequest: boolean;
  displayInternal: boolean;
  displayExternal: boolean;
  displayAggregates: boolean;
  displayCement: boolean;
  displayReadyMix: boolean;
}

const FlexSystemWrapper = (props: {
  original: { highlight: string };
  classes: string[];
  value: boolean;
}) => {
  if (props.original.highlight) {
    props.classes[1] = 'new-highlight';
  }
  return props.value ? (
    <i aria-hidden="true" data-test="admin-truck-is-flex-system" className={cx('icon-check')} />
  ) : (
    <i
      aria-hidden="true"
      data-test="admin-truck-is-flex-system"
      className={cx('icon-close', styles.notEnabled)}
    />
  );
};

const TruckHomePlantWrapper = (props: { value: { url?: string; name?: string } }) =>
  addDataTest(
    null,
    'admin-truck-home-plant',
    props.value ? TrucksView.plantUrlAccessor(props.value) : '',
  );

const GlinxEnabledWrapper = (props: {
  original: { highlight: string };
  classes: string[];
  value: boolean;
}) => {
  if (props.original.highlight) {
    props.classes[1] = 'new-highlight';
  }
  return props.value ? (
    <i aria-hidden="true" data-test="admin-truck-is-glinx-enabled" className={cx('icon-check')} />
  ) : (
    <i
      aria-hidden="true"
      data-test="admin-truck-is-glinx-enabled"
      className={cx('icon-close', styles.notEnabled)}
    />
  );
};

export class TrucksView extends Component<TrucksViewProps, TrucksViewState> {
  static defaultProps = {
    plantList: [],
    drivers: [],
    trucksPermissionAccess: '',
    tab: 0,
  };

  static getDisplayStateFromQueryParams = (queryParams?: any) => {
    const isExternal = queryParams?.isExternal;
    const productLinesCsv = queryParams?.productLines;

    const displayState: any = {};

    if (isExternal === 'true') {
      displayState.displayInternal = false;
      displayState.displayExternal = true;
    }
    if (isExternal === 'false') {
      displayState.displayInternal = true;
      displayState.displayExternal = false;
    }
    if (queryParams && isExternal === undefined) {
      displayState.displayInternal = true;
      displayState.displayExternal = true;
    }

    if (typeof productLinesCsv === 'string') {
      const selected = productLinesCsv.length > 0 ? productLinesCsv.split(',') : [];
      displayState.displayAggregates = selected.includes(ProductLine.Aggregates);
      displayState.displayCement = selected.includes(ProductLine.Cement);
      displayState.displayReadyMix = selected.includes(ProductLine.ReadyMix);
    }

    return Object.keys(displayState).length > 0 ? displayState : null;
  };

  constructor(props: TrucksViewProps) {
    super(props);
    const displayState = TrucksView.getDisplayStateFromQueryParams(props.savedQueryParams);
    this.state = {
      isOpen: false,
      isCloseRequest: false,
      displayInternal: displayState?.displayInternal ?? true,
      displayExternal: displayState?.displayExternal ?? false,
      displayAggregates: displayState?.displayAggregates ?? false,
      displayCement: displayState?.displayCement ?? false,
      displayReadyMix: displayState?.displayReadyMix ?? false,
    };
  }

  async componentDidMount() {
    this.props.setReduxDriverList(this.props.currentRegion!.url!);
    const hasSavedProductLines = typeof this.props.savedQueryParams?.productLines === 'string';
    if (!hasSavedProductLines) {
      this.setProductLineCheckboxes();
    }
  }

  componentDidUpdate(prevProps: any) {
    if (prevProps.currentRegion?.url !== this.props.currentRegion?.url) {
      const hasSavedProductLines = typeof this.props.savedQueryParams?.productLines === 'string';
      if (!hasSavedProductLines) {
        this.setProductLineCheckboxes();
      }
    }
  }

  setProductLineCheckboxes() {
    const productLineDisplay = {
      displayAggregates: true,
      displayCement: true,
      displayReadyMix: true,
    };

    this.setState({ ...productLineDisplay });
  }

  static plantUrlAccessor = (plant: { url?: string; name?: string }) =>
    plant.url ? plant.name : NOT_AVAILABLE;

  static driverUrlAccessor = (driver: { url?: string; name?: string }) => {
    if (!driver.name) {
      return NOT_AVAILABLE;
    }
    return TrucksView.formatDriverName(driver.name);
  };

  static formatDriverName = (driverName?: string) => {
    const name = driverName?.trim().split(' ') ?? [];

    const firstName = name[name.length - 1] ?? '';
    delete name[name.length - 1];
    const lastName = name.join(' ').trim();

    return `${[lastName, firstName].join(', ')}`;
  };

  handleDisplayCheckChange = (event: any) => {
    const { displayInternal, displayExternal } = this.state;
    const { target } = event;
    const { name, checked } = target;
    const reverseInternalValue = name === 'displayInternal' && !checked && !displayExternal;
    const reverseExternalValue = name === 'displayExternal' && !checked && !displayInternal;
    const displayList = [
      'displayInternal',
      'displayExternal',
      'displayAggregates',
      'displayCement',
      'displayReadyMix',
    ];

    if (reverseInternalValue) {
      this.setState(
        {
          displayInternal: checked,
          displayExternal: true,
        },
        this.persistQueryParams,
      );
    } else if (reverseExternalValue) {
      this.setState(
        {
          displayInternal: true,
          displayExternal: checked,
        },
        this.persistQueryParams,
      );
    } else if (displayList.indexOf(name) !== -1) {
      if (name === 'displayInternal') {
        this.setState({ displayInternal: checked }, this.persistQueryParams);
      }

      if (name === 'displayExternal') {
        this.setState({ displayExternal: checked }, this.persistQueryParams);
      }

      if (name === 'displayAggregates') {
        this.setState({ displayAggregates: checked }, this.persistQueryParams);
      }

      if (name === 'displayCement') {
        this.setState({ displayCement: checked }, this.persistQueryParams);
      }

      if (name === 'displayReadyMix') {
        this.setState({ displayReadyMix: checked }, this.persistQueryParams);
      }
    }
  };

  getQueryParams = () => {
    const { displayInternal, displayExternal, displayAggregates, displayCement, displayReadyMix } =
      this.state;
    let params = {};
    if (Number(displayInternal) + Number(displayExternal) === 1) {
      params = { isExternal: `${displayInternal ? 'false' : 'true'}` };
    }
    const queryProductLines = [];
    if (displayAggregates) queryProductLines.push(ProductLine.Aggregates);
    if (displayCement) queryProductLines.push(ProductLine.Cement);
    if (displayReadyMix) queryProductLines.push(ProductLine.ReadyMix);
    params = { productLines: queryProductLines.join(','), ...params };
    return params;
  };

  persistQueryParams = () => {
    this.props.setDataTableQueryParams(this.getQueryParams(), DATASOURCE.VEHICLE);
  };

  static getColumns = () => [
    {
      Header: addHeaderDataTest(null, 'sort-truck-by-alias', ADMIN_LABELS.VEHICLE_ALIAS),
      accessor: ADMIN_KEYS.TRUCK_ALIAS,
      minWidth: 130,
      Cell: (props: { original: { highlight: string }; classes: string[]; value: string }) => {
        if (props.original.highlight) {
          props.classes[1] = 'new-highlight';
        }
        return addDataTest(null, 'admin-truck-alias', props.value);
      },
    },
    {
      Header: addHeaderDataTest(null, 'sort-truck-by-id', ADMIN_LABELS.EXTERNAL_ID),
      accessor: ADMIN_KEYS.EXTERNAL_ID,
      Cell: (props: { original: { highlight: string }; classes: string[]; value: string }) => {
        if (props.original.highlight) {
          props.classes[1] = 'new-highlight';
        }
        return addDataTest(null, 'admin-external-id', props.value);
      },
    },
    {
      Header: addHeaderDataTest(null, 'sort-truck-by-fleet', ADMIN_LABELS.OWNERSHIP),
      id: ADMIN_KEYS.IS_EXTERNAL,
      accessor: (row: any) => row.isExternal,
      sortable: true,
      Cell: (props: { original: { highlight: string }; classes: string[]; value: boolean }) => {
        if (props.original.highlight) {
          props.classes[1] = 'new-highlight';
        }
        return addDataTest(null, 'admin-truck-fleet', props.value ? 'External' : 'Internal');
      },
    },
    {
      Header: addHeaderDataTest(null, 'sort-truck-by-primary', ADMIN_LABELS.PRODUCT_LINE),
      id: ADMIN_KEYS.PRIMARY_PRODUCT_LINE,
      accessor: (row: any) => accessValue(ADMIN_KEYS.PRIMARY_PRODUCT_LINE, row),
      Cell: (props: { original: { highlight: string }; classes: string[]; value: string }) => {
        if (props.original.highlight) {
          props.classes[1] = 'new-highlight';
        }
        return addDataTest(null, 'admin-truck-primary-product-line', props.value || NOT_AVAILABLE);
      },
    },
    {
      Header: addHeaderDataTest(null, 'sort-truck-by-vehicletype', ADMIN_LABELS.VEHICLE_TYPE),
      id: ADMIN_KEYS.VEHICLE_TYPE_NAME,
      accessor: (row: any) => accessValue(ADMIN_KEYS.VEHICLE_TYPE_NAME, row),
      sortable: true,
      Cell: (props: { original: { highlight: string }; classes: string[]; value: string }) => {
        if (props.original.highlight) {
          props.classes[1] = 'new-highlight';
        }
        return addDataTest(null, 'admin-truck-vehicle-type', props.value || NOT_AVAILABLE);
      },
    },
    {
      Header: addHeaderDataTest(null, 'truck-by-is-haulerName-enabled', ADMIN_LABELS.HAULER_NAME),
      id: ADMIN_KEYS.HAULERS,
      accessor: (row: any) => accessValue(ADMIN_KEYS.HAULERS, row),
      sortable: false,
      className: styles.CenterCell,
      Cell: (props: { original: { highlight: string }; classes: string[]; value: any[] }) => {
        if (props.original.highlight) {
          props.classes[1] = 'new-highlight';
        }
        let displayValue;
        let tooltipContent;

        if (Array.isArray(props.value) && props.value.length > 0) {
          // Extract the first hauler's name
          const topHauler = props.value[0]?.name || NOT_AVAILABLE;
          const allHaulersList = props.value.map(hauler => hauler.name).join(', ');
          // Prepare tooltip content for the rest of the haulers
          const remainingHaulers = props.value
            .slice(1)
            .map(hauler => hauler.name)
            .join(', ');
          tooltipContent = remainingHaulers ? `Other haulers: ${remainingHaulers}` : '';

          displayValue = (
            <>
              {
                tooltipContent ? (
                  <Tooltip
                    key={topHauler}
                    text={tooltipContent}
                    placement="top"
                    enterDelay={600}
                    classes={{
                      popper: styles.lockPopper,
                      tooltip: styles.tooltipFont,
                    }}
                  >
                    <span>{allHaulersList}</span>
                  </Tooltip>
                ) : (
                  <span>{allHaulersList}</span>
                ) // Directly display the single hauler's name
              }
            </>
          );
        } else {
          displayValue = NOT_AVAILABLE;
        }
        return addDataTest(null, 'admin-truck-hauler-name', displayValue);
      },
    },
    {
      Header: addHeaderDataTest(null, 'sort-truck-by-is-glinx-enabled', ADMIN_LABELS.GLINX_ENABLED),
      id: ADMIN_KEYS.IS_GLINX_ENABLED,
      accessor: (row: any) => row.isGlinxEnabled,
      sortable: true,
      className: styles.CenterCell,
      Cell: GlinxEnabledWrapper,
    },
    {
      Header: addHeaderDataTest(null, 'sort-truck-by-is-flex-system', ADMIN_LABELS.FLEX_SYSTEM),
      id: ADMIN_KEYS.IS_FLEX_SYSTEM,
      accessor: (row: any) => row.isFlexSystem,
      sortable: true,
      className: styles.CenterCell,
      Cell: FlexSystemWrapper,
    },
    {
      Header: addHeaderDataTest(null, 'truck-home-plant-header', ADMIN_LABELS.HOME_PLANT),
      id: ADMIN_KEYS.PLANT,
      accessor: (row: any) => accessValue(ADMIN_KEYS.PLANT, row),
      minWidth: 135,
      sortable: true,
      Cell: TruckHomePlantWrapper,
    },
    {
      Header: addHeaderDataTest(null, 'truck-default-driver-header', ADMIN_LABELS.DEFAULT_DRIVER),
      id: ADMIN_KEYS.DEFAULT_DRIVER,
      accessor: (row: any) => accessValue(ADMIN_KEYS.DEFAULT_DRIVER, row),
      minWidth: 125,
      sortable: true,
      Cell: (props: { value: { url?: string } }) =>
        addDataTest(
          null,
          'admin-truck-default-driver',
          props.value ? TrucksView.driverUrlAccessor(props.value) : '',
        ),
    },
  ];

  requestCloseModal = () => {
    this.setState({
      isOpen: true,
      isCloseRequest: true,
    });
  };

  closeModal = () => {
    this.setState({
      isOpen: false,
      isCloseRequest: false,
    });
  };

  openModal = () => {
    this.setState({
      isOpen: true,
      isCloseRequest: false,
    });
  };

  handleTabChange = (_event: any, newValue: number) => {
    if (newValue === 1) {
      this.props.history.push('/admin/trucks/deactivated');
    } else if (newValue === 0) {
      this.props.history.push('/admin/trucks');
    }
  };

  render() {
    const columns = TrucksView.getColumns();
    const hasPermissionAccess = this.props.trucksPermissionAccess === PermissionAccess.Edit;

    const tabs = (
      <Tabs
        value={this.props.tab}
        onChange={this.handleTabChange}
        classes={{
          root: styles.tabsContainer,
        }}
      >
        <Tab label="Active" data-test="trucks-active-tab" />
        <Tab label="Deactivated" data-test="trucks-deactivated-tab" />
      </Tabs>
    );

    const headerRightControls = (
      <>
        <span className={styles.checkboxTitle}>Fleet:</span>
        <FormControlLabel
          data-test="truck-fleet-filter-internal"
          control={
            <Checkbox
              checked={this.state.displayInternal}
              onChange={this.handleDisplayCheckChange}
              name="displayInternal"
              color="primary"
            />
          }
          label={<span className={styles.checkboxLabel}>Internal</span>}
          classes={{
            root: styles.checkboxLabelContainer,
          }}
        />
        <FormControlLabel
          data-test="truck-fleet-filter-external"
          control={
            <Checkbox
              checked={this.state.displayExternal}
              onChange={this.handleDisplayCheckChange}
              name="displayExternal"
              color="primary"
            />
          }
          label={<span className={styles.checkboxLabel}>External</span>}
          classes={{
            root: styles.checkboxLabelContainer,
          }}
        />

        <div className={styles.checkboxSpacing} />

        <span className={styles.checkboxTitle}>Product Line:</span>
        <FormControlLabel
          data-test="truck-product-line-filter-agg"
          control={
            <Checkbox
              checked={this.state.displayAggregates}
              onChange={this.handleDisplayCheckChange}
              name="displayAggregates"
              color="primary"
            />
          }
          label={<span className={styles.checkboxLabel}>AGG</span>}
          classes={{
            root: styles.checkboxLabelContainer,
          }}
        />

        <FormControlLabel
          data-test="truck-product-line-filter-cem"
          control={
            <Checkbox
              checked={this.state.displayCement}
              onChange={this.handleDisplayCheckChange}
              name="displayCement"
              color="primary"
            />
          }
          label={<span className={styles.checkboxLabel}>CEM</span>}
          classes={{
            root: styles.checkboxLabelContainer,
          }}
        />

        <FormControlLabel
          data-test="truck-product-line-filter-rm"
          control={
            <Checkbox
              checked={this.state.displayReadyMix}
              onChange={this.handleDisplayCheckChange}
              name="displayReadyMix"
              color="primary"
            />
          }
          label={<span className={styles.checkboxLabel}>RM</span>}
          classes={{
            root: styles.checkboxLabelContainer,
          }}
        />
      </>
    );

    const props: any = {
      baseUrl: getGeotraxBaseUrl() + TRUCK_PROJECTIONS,
      columns,
      header: VEHICLES_TEXT,
      permissionKey: TRUCKS_TEXT,
      renderTabs: tabs,
      headerRightControls,
      isArchived: this.props.tab === 1,
      canAddNewItem: hasPermissionAccess,
      queryParams: this.getQueryParams(),
      bypassCache: true,
      source: DATASOURCE.VEHICLE,
      enableExport: true,
      exportedFileName: 'vehicles',
    };

    return (
      <div className={styles.container}>
        <DataTableContent {...props} />
        {hasPermissionAccess && (
          <TrucksAddModal
            isOpen={this.state.isOpen}
            openModal={this.openModal}
            closeModal={this.requestCloseModal}
            drivers={this.props.drivers}
            discardAcceptAction={this.closeModal}
            discardRejectAction={this.openModal}
            isCloseRequest={this.state.isCloseRequest}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  currentRegion: state.currentRegion,
  regions: state.assignedRegionList,
  plantList: state.plantList,
  drivers: state.driverList.drivers,
  trucksPermissionAccess: state.adminPermissionAccess[TRUCKS_TEXT],
  savedQueryParams: state.dataTableQueryParams?.[DATASOURCE.VEHICLE]?.queryParams,
});

type TrucksViewReduxProps = ReturnType<typeof mapStateToProps>;

type TrucksViewDispatchProps = {
  setReduxDriverList: ConnectedDispatchFunction<typeof setReduxDriverList>;
  setDataTableQueryParams: (queryParams: any, source: string) => void;
};

interface History {
  push: (p: string) => void;
}

export interface TrucksViewOwnProps {
  plantList?: PlantDto[];
  drivers?: DriverDto[];
  trucksPermissionAccess?: string;
  history: History;
  tab: number;
}

export type TrucksViewProps = TrucksViewReduxProps & TrucksViewDispatchProps & TrucksViewOwnProps;

export default connect(mapStateToProps, { setReduxDriverList, setDataTableQueryParams })(
  TrucksView,
);
